import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../api";
import { useTranslation } from "../../../hooks/useTranslation";
import { useInView } from "../../../hooks/useInView";
import { Skeleton } from "../../Ui/Skeleton/Skeleton";
import GuideCard from "../../Guides/GuideCard";
import { guidesListPath } from "../../../utils/posts";

const GUIDES_LIMIT = 3;

const GuideCardSkeleton = () => (
  <div className="card elev-sm" style={{ padding: 0, overflow: "hidden" }}>
    <Skeleton height={200} radius={0} />
    <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 8 }}>
      <Skeleton width="40%" height={12} />
      <Skeleton width="70%" height={19} />
      <Skeleton width="90%" height={14} />
      <Skeleton width="60%" height={14} />
    </div>
  </div>
);

/**
 * Homepage "Latest Guides" section — establishes the editorial side of
 * Small Potatoes (curated ways to experience a city) alongside the
 * existing Places-directory experience. Reuses the Phase 2 public Posts
 * API and GuideCard component; adds no new backend surface.
 *
 * City-aware: uses the same `selectedCity` LandingPage already owns and
 * shares with PicksSection/EventsSection, rather than inventing new state.
 * A Guide API failure is swallowed (logged, section hidden) so it can
 * never break the rest of the homepage — same fail-quiet convention as
 * PicksSection/EventsSection/OffersSection.
 *
 * @component
 */
const GuidesSection = ({ selectedCity }) => {
  const { t } = useTranslation();
  const [ref, isInView] = useInView(0, "250px 0px");
  const [guides, setGuides] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!isInView) return;
    const params = { limit: GUIDES_LIMIT };
    if (selectedCity) params.city = selectedCity;
    api
      .getPublicPosts(params)
      .then((res) => setGuides(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        console.error("Error fetching guides for landing page:", err);
        setHasError(true);
      })
      .finally(() => setIsLoading(false));
  }, [isInView, selectedCity]);

  // 0 published Guides (for this city, or at all) — hide the whole section
  // rather than showing an empty editorial block. A fetch failure hides it
  // the same way; the dedicated /guides page is where errors get surfaced.
  if (!isLoading && (hasError || guides.length === 0)) return null;

  return (
    <section id="guides" className="sp-section" style={{ paddingLeft: 32, paddingRight: 32 }}>
      <div style={{ maxWidth: 1240, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 20,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div className="tag tag-accent">{t.landingGuidesKicker}</div>
            <div className="sp-guides-heading-row">
              <h2 style={{ marginTop: 10 }}>{t.landingGuidesHeading}</h2>
              {/* Mobile-only — sits next to the heading below the 860px
                  breakpoint (see LandingPage.css); the desktop CTA below
                  (.sp-guides-cta) is hidden there instead. */}
              {!isLoading && guides.length > 0 && (
                <Link className="btn btn-secondary sp-guides-cta-mobile" to={guidesListPath(selectedCity)}>
                  {t.landingGuidesCtaMore}
                </Link>
              )}
            </div>
            <p style={{ opacity: 0.75 }}>{t.landingGuidesSubtitle}</p>
          </div>
          {!isLoading && guides.length > 0 && (
            <Link className="btn btn-secondary sp-guides-cta" to={guidesListPath(selectedCity)}>
              {t.landingGuidesCtaMore}
            </Link>
          )}
        </div>
        <div
          ref={ref}
          className="sp-cards-grid"
          style={{
            display: "grid",
            // auto-fit + a max in minmax: with fewer than 3 Guides, unused
            // tracks collapse instead of stretching the 1-2 real cards (or
            // leaving obviously empty columns) across the full row — 1-2
            // Guides render at their natural card width instead of looking
            // like a broken/incomplete 3-up grid. .sp-cards-grid's existing
            // media-query overrides (LandingPage.css) still force 2 then 1
            // column below desktop.
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 360px))",
            gap: 24,
            marginTop: 30,
          }}
        >
          {isLoading && Array.from({ length: GUIDES_LIMIT }).map((_, i) => <GuideCardSkeleton key={i} />)}
          {!isLoading && guides.map((post) => <GuideCard key={post._id} post={post} compact />)}
        </div>
      </div>
    </section>
  );
};

export default GuidesSection;
